import React from "react"
import camelCase from "lodash/camelCase"

import RichText from "legacy/components/shared/RichText/RichText"
import useBreakpoints from "src/legacy/hooks/useBreakpoints"
import { Element, Image } from "legacy/components/shared/base"
import { FiftyFiftySplitType } from "types/shared-components"
import { LinkSwitch } from "legacy/components/Redesign/Link"
import { Maybe } from "types/Maybe"
import { Title } from "legacy/components/Redesign/Title/Title"

export type FiftyFiftySplitProps = {
  data: Maybe<FiftyFiftySplitType>
}

export const FiftyFiftySplit: React.FC<FiftyFiftySplitProps> = ({
  data,
}): React.ReactElement | null => {
  if (!data?.primary) return null

  const { breakpoint } = useBreakpoints()

  const {
    background,
    button_style,
    button_text,
    button_type,
    button_url,
    center_image,
    eyebrow,
    fifty_fifty_intro,
    full_sized_image,
    fifty_fifty_split_title,
    image,
    image_mobile_placement,
    image_placement,
    mobile_text_alignment,
    title_size,
  } = data.primary

  const imageHeight = image?.dimensions?.height
  const imageWidth = image?.dimensions?.width
  const imageAlt = image?.alt
  const imageUrl = image?.url

  const smBreakpoint = breakpoint === "sm"
  const middlePlacedMobileImage = smBreakpoint && image_mobile_placement

  const bgColor = background ? camelCase(background) : "inherit"

  return (
    <Element bg={bgColor} px="sm">
      <Element
        display={["block", "grid"]}
        gridTemplateColumns={smBreakpoint ? "none" : "1fr 1fr"}
        maxWidth="contentWidth"
        mx="auto"
        textAlign={[mobile_text_alignment, mobile_text_alignment, "left"]}
      >
        {!middlePlacedMobileImage && imageAlt && imageUrl && (
          <Element
            alignItems="center"
            display={["block", "grid"]}
            mb={["md", "0"]}
            ml={smBreakpoint ? "0" : `${image_placement ? "0" : "xxlg"}`}
            mr={
              !smBreakpoint &&
              image_placement &&
              !full_sized_image &&
              !center_image
                ? "xxlg"
                : "0"
            }
            justifyContent={center_image ? "center" : "normal"}
            order={!image_placement || smBreakpoint ? 0 : 1}
            textAlign="center"
          >
            <Image
              aspectRatio={`${imageWidth} / ${imageHeight}`}
              imageData={{
                alt: imageAlt,
                url: imageUrl,
              }}
              width="480px"
            />
          </Element>
        )}
        <Element
          ml={smBreakpoint ? "0" : `${image_placement ? "0" : "xxlg"}`}
          mr={smBreakpoint ? "0" : `${image_placement ? "xxlg" : "0"}`}
          my={smBreakpoint ? "sm" : "xlg"}
        >
          {eyebrow && (
            <Element
              color="offBlack"
              fontFamily="martel"
              fontSize="16px"
              fontWeight="600"
              lineHeight="28px"
              letterSpacing="wide"
              mb={["xs", "md"]}
              textTransform="uppercase"
            >
              {eyebrow}
            </Element>
          )}
          {fifty_fifty_split_title?.text && (
            <Title
              mb="sm"
              title={fifty_fifty_split_title?.text}
              titleSize={title_size}
            />
          )}
          {middlePlacedMobileImage && imageAlt && imageUrl && (
            <Element
              alignItems="center"
              display={["block", "grid"]}
              mb={["md", "0"]}
              mx="auto"
              order={image_placement ? [0, 1] : 0}
              textAlign="center"
            >
              <Image
                aspectRatio={`${imageWidth} / ${imageHeight}`}
                imageData={{
                  alt: imageAlt,
                  url: imageUrl,
                }}
                width="720px"
              />
            </Element>
          )}
          {fifty_fifty_intro && (
            <RichText
              color="offBlack"
              fontFamily="martel"
              fontSize={["17px", "18px"]}
              fontWeight="400"
              lineHeight={["28px", "32px"]}
              render={fifty_fifty_intro?.richText}
            />
          )}
          {button_text && button_url?.url && (
            <LinkSwitch
              linkType={button_type}
              buttonType={button_type}
              mt={button_style === "secondary" ? "sm" : ["md", "lg"]}
              url={button_url}
              variant={button_style}
            >
              {button_text}
            </LinkSwitch>
          )}
        </Element>
      </Element>
    </Element>
  )
}
